import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Typography, Button, Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../../../context/UserContext';
import DropdownWithIcon from '../../../components/DropdownWithIcon';
import hospitalIcon from '../../../assets/protocols/hospital.svg';
import './AntibiogramConfig.scss';
import CommonLayout from '../../../components/CommonLayout';
import API from '../../../config/axiosConfig';
import Loader from '../../../components/Loader';
import { FaCheck } from 'react-icons/fa';
import coinsIcon from '../../../assets/coins.png';
import layoutIcon from '../../../assets/Layout.png';
import dropletIcon from '../../../assets/Droplet.png';
import infoIcon from '../../../assets/info.png';
import ConfirmationDialog from '../../../components/dialogs/ConfirmationDialog';

// Import extracted components
import ArrowLeftIcon from './components/ArrowLeftIcon';
import DrugsTab from './components/DrugsTab';
import CategoriesTab from './components/CategoriesTab';
import ProtocolsSection from './components/ProtocolsSection';
import FloatingPreview from '../../../components/FloatingPreview/FloatingPreview';
import PreviewPane from './components/PreviewPane';

const AntibiogramConfig = () => {
  const [selectedHospital, setSelectedHospital] = useState('');
  const [availableHospitals, setAvailableHospitals] = useState([]);
  const [hospitalsWithEditAccess, setHospitalsWithEditAccess] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Data state
  const [drugs, setDrugs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [blacklists, setBlacklists] = useState({
    'trimester 1': [],
    'trimester 2': [],
    'trimester 3': [],
    'nursing': []
  });
  const [protocols, setProtocols] = useState([]);

  // Editing states
  const [editingDrug, setEditingDrug] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newDrug, setNewDrug] = useState({ genericName: '', displayName: '' });
  const [newCategory, setNewCategory] = useState({ name: '', imagePath: '' });
  const [activeTab, setActiveTab] = useState(null); // will be set after user loads
  const [isEditingProtocol, setIsEditingProtocol] = useState(false);
  const [focusedProtocol, setFocusedProtocol] = useState(null);
  const [focusedDiagnosis, setFocusedDiagnosis] = useState(null);
  const [drugPreviewOverride, setDrugPreviewOverride] = useState(null);
  const [unsavedDialog, setUnsavedDialog] = useState({ open: false, targetTab: null });
  const drugSaveRef = useRef(null);
  const drugDirtyRef = useRef(false);

  // User context for permissions
  const { user, hospitals, permittedApps } = useContext(UserContext);

  const APP_NAME = 'ANTIBIOGRAM';
  const CONFIG_NAME = 'antibiogram-admin';
  const CATEGORIES_CONFIG_NAME = 'categories';

  // Unused pendingNavigation states removed

  // Generate unique ID for drugs
  const generateDrugId = () => {
    return `drug_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Generate unique ID for categories
  const generateCategoryId = () => {
    return `category_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  useEffect(() => {
    if (hospitals && hospitals.length > 0 && user && permittedApps) {
      const antibiogramApp = permittedApps.find(app => app.key === APP_NAME);

      let editableHospitals = [];

      if (user.isSuperAdmin) {
        editableHospitals = [...hospitals];
      } else if (antibiogramApp) {
        const permittedHospitalIds = antibiogramApp.admins
          .filter(admin => (admin.user._id === user._id))
          .map(admin => admin.hospital._id);

        editableHospitals = hospitals.filter(hospital =>
          permittedHospitalIds.includes(hospital._id)
        );
      }

      setHospitalsWithEditAccess(editableHospitals);

      let hospitalOptions = [...hospitals];

      if (user.isSuperAdmin) {
        hospitalOptions.push({
          _id: "apps",
          nameHebrew: "ברירת מחדל",
          nameEnglish: "apps"
        });
      }

      setAvailableHospitals(hospitalOptions);

      if (editableHospitals.length === 1) {
        setSelectedHospital(editableHospitals[0].nameEnglish);
      } else if (user.isSuperAdmin && hospitalOptions.length > 0) {
        setSelectedHospital("apps");
      } else if (editableHospitals.length > 0) {
        setSelectedHospital(editableHospitals[0].nameEnglish);
      } else if (hospitalOptions.length > 0) {
        setSelectedHospital(hospitalOptions[0].nameEnglish);
      }
    }
  }, [hospitals, user, permittedApps]);

  // Set default active tab based on user role
  useEffect(() => {
    if (user && activeTab === null) {
      setActiveTab(user.isSuperAdmin ? 'categories' : 'drugs');
    }
  }, [user, activeTab]);

  useEffect(() => {
    if (selectedHospital) {
      fetchAntibiogramData(selectedHospital);
    }
  }, [selectedHospital]);

  // Redirect away from categories tab if user is not super admin
  useEffect(() => {
    if (activeTab === 'categories' && user && !user.isSuperAdmin) {
      setActiveTab('drugs');
    }
  }, [user, activeTab]);

  const fetchAntibiogramData = async (hospital) => {
    if (!hospital) return;

    try {
      setLoading(true);

      const response = await API.get(`/api/configs/${APP_NAME}/${CONFIG_NAME}/${hospital}`);
      const data = response.data.success ? (response.data.data || {}) : {};

      setDrugs(data.drugs || []);
      setBlacklists(data.antibiohelpConfig?.blacklists || {
        'trimester 1': [],
        'trimester 2': [],
        'trimester 3': [],
        'nursing': []
      });
      setProtocols(data.antibiohelpConfig?.protocols || []);

      try {
        const categoriesResponse = await API.get(`/api/configs/${APP_NAME}/${CATEGORIES_CONFIG_NAME}/${hospital}`);
        if (categoriesResponse.data.success) {
          const categoriesData = categoriesResponse.data.data || {};
          setCategories(categoriesData.categories || []);
        } else {
          setCategories([]);
        }
      } catch (categoriesError) {
        console.error('Error fetching categories data:', categoriesError);
        const oldCategories = data.antibiohelpConfig?.categories || [];
        setCategories(oldCategories.length > 0 ? oldCategories : []);
      }
    } catch (error) {
      console.error('Error fetching antibiogram data:', error);
      setDrugs([]);
      setCategories([]);
      setBlacklists({
        'trimester 1': [],
        'trimester 2': [],
        'trimester 3': [],
        'nursing': []
      });
      setProtocols([]);
    } finally {
      setLoading(false);
    }
  };

  const saveAntibiogramData = async (overrides = {}) => {
    if (!selectedHospital) {
      showNotification('יש לבחור בית חולים תחילה', 'error');
      return false;
    }

    try {
      setSaving(true);

      const currentDrugs = overrides.drugs || drugs;
      const currentBlacklists = overrides.blacklists || blacklists;
      const currentProtocols = overrides.protocols || protocols;
      const currentCategories = overrides.categories || categories;

      const dataToSave = {
        drugs: currentDrugs,
        antibiohelpConfig: {
          blacklists: currentBlacklists,
          protocols: currentProtocols
        }
      };

      const response = await API.post(`/api/configs/${APP_NAME}/${CONFIG_NAME}/${selectedHospital}`, {
        data: dataToSave
      });

      let categoriesResponse = null;
      if (user?.isSuperAdmin) {
        const categoriesDataToSave = {
          categories: currentCategories
        };

        categoriesResponse = await API.post(`/api/configs/${APP_NAME}/${CATEGORIES_CONFIG_NAME}/${selectedHospital}`, {
          data: categoriesDataToSave
        });
      }

      if (response.data.success && (categoriesResponse === null || categoriesResponse.data.success)) {
        showNotification('השינויים נשמרו בהצלחה', 'success');
        
        // Update state with saved data
        if (overrides.drugs) setDrugs(overrides.drugs);
        if (overrides.blacklists) setBlacklists(overrides.blacklists);
        if (overrides.protocols) setProtocols(overrides.protocols);
        if (overrides.categories && user?.isSuperAdmin) setCategories(overrides.categories);

        return true;
      } else {
        showNotification('שגיאה בשמירת הנתונים', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error saving antibiogram data:', error);
      showNotification('שגיאה בשמירת הנתונים', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });

    setTimeout(() => {
      setNotification(prev => ({ ...prev, open: false }));
    }, 3000);
  };

  const handleHospitalChange = (event) => {
    const newHospital = event.target.value;
    setDrugPreviewOverride(null);
    setSelectedHospital(newHospital);
  };

  const getAppLink = () => {
    return `https://my.mashlom.me/main/apps/antibiogram?hospital=${selectedHospital}`;
  };

  // Drugs management
  const handleAddDrug = async (extra) => {
    if (newDrug.genericName && newDrug.displayName) {
      const drugId = generateDrugId();
      const drugWithId = {
        id: drugId,
        genericName: newDrug.genericName,
        displayName: newDrug.displayName
      };
      const updatedDrugs = [...drugs, drugWithId];

      let updatedBlacklists = blacklists;

      if (extra?.blacklists) {
        updatedBlacklists = { ...blacklists };
        Object.keys(extra.blacklists).forEach(key => {
          if (extra.blacklists[key] && !(updatedBlacklists[key] || []).includes(drugId)) {
            updatedBlacklists[key] = [...(updatedBlacklists[key] || []), drugId];
          }
        });
      }

      const success = await saveAntibiogramData({
        drugs: updatedDrugs,
        blacklists: updatedBlacklists
      });

      if (success) {
        setNewDrug({ genericName: '', displayName: '' });
      }
      return success;
    }
    return false;
  };

  const handleEditDrug = (index) => {
    setEditingDrug(index);
  };

  const handleDrugPreviewChange = useCallback((draft) => {
    setDrugPreviewOverride(draft);
  }, []);

  const handleSaveDrug = async (index, updatedDrug, newBlacklists, _newAllergy, oldBlacklists) => {
    const updatedDrugs = [...drugs];
    const drugId = updatedDrug.id || generateDrugId();
    updatedDrugs[index] = {
      ...updatedDrug,
      id: drugId
    };

    let updatedBlacklistsObj = { ...blacklists };

    // Process blacklists if passed from child
    if (newBlacklists && oldBlacklists) {
      Object.keys(oldBlacklists).forEach(key => {
        const wasIn = oldBlacklists[key];
        const shouldBeIn = newBlacklists[key];

        if (shouldBeIn && !wasIn) {
          updatedBlacklistsObj[key] = [...(updatedBlacklistsObj[key] || []), drugId];
        } else if (!shouldBeIn && wasIn) {
          updatedBlacklistsObj[key] = (updatedBlacklistsObj[key] || []).filter(id => id !== drugId);
        }
      });
    }

    const success = await saveAntibiogramData({
      drugs: updatedDrugs,
      blacklists: updatedBlacklistsObj
    });

    if (success) {
      setEditingDrug(null);
    }
    return success;
  };

  const handleDeleteDrug = async (index) => {
    const drugId = drugs[index].id;
    const updatedBlacklists = { ...blacklists };
    Object.keys(updatedBlacklists).forEach(key => {
      updatedBlacklists[key] = updatedBlacklists[key].filter(id => id !== drugId);
    });

    const success = await saveAntibiogramData({
      drugs: drugs.filter((_, i) => i !== index),
      blacklists: updatedBlacklists
    });
    return success;
  };

  // Categories management
  const handleAddCategory = async () => {
    if (newCategory.name && newCategory.imagePath) {
      const categoryWithId = {
        id: generateCategoryId(),
        name: newCategory.name,
        imagePath: newCategory.imagePath
      };
      const updatedCategories = [...categories, categoryWithId];
      const success = await saveAntibiogramData({ categories: updatedCategories });
      if (success) {
        setNewCategory({ name: '', imagePath: '' });
      }
      return success;
    }
    return false;
  };

  const handleEditCategory = (index) => {
    setEditingCategory(index);
  };

  const handleSaveCategory = async (index, updatedCategory) => {
    const updated = [...categories];
    updated[index] = {
      ...updatedCategory,
      id: updatedCategory.id || generateCategoryId()
    };
    const success = await saveAntibiogramData({ categories: updated });
    if (success) {
      setEditingCategory(null);
    }
    return success;
  };

  const handleDeleteCategory = async (index) => {
    const categoryId = categories[index].id;
    const updatedProtocols = protocols.map(protocol => {
      if (protocol.category === categoryId) {
        return { ...protocol, category: '' };
      }
      return protocol;
    });
    const updatedCategories = categories.filter((_, i) => i !== index);
    
    return await saveAntibiogramData({
      categories: updatedCategories,
      protocols: updatedProtocols
    });
  };

  // Legacy blacklist/allergies handlers - no longer directly used from Tabs UI.
  // The tab handles this within handleSaveDrug.
  
  const handleSetProtocols = async (updated) => {
    return await saveAntibiogramData({ protocols: updated });
  };

  const getDrugDisplayName = (drugId) => {
    const drug = drugs.find(d => d.id === drugId);
    return drug ? drug.displayName : drugId;
  };

  const showHospitalDropdown = user?.isSuperAdmin || hospitalsWithEditAccess.length > 1;
  const previewDrugs = drugPreviewOverride?.drugs || drugs;
  const previewBlacklists = drugPreviewOverride?.blacklists || blacklists;

  const handleFocusChange = (protocol, diagnosis) => {
    setFocusedProtocol(protocol);
    setFocusedDiagnosis(diagnosis);
  };

  const TAB_NAMES = { drugs: 'תרופות', categories: 'קטגוריות', protocols: 'פרוטוקולים' };

  const isDirty = () => {
    if (activeTab === 'drugs') return drugDirtyRef.current || newDrug.genericName.trim() !== '';
    if (activeTab === 'categories') return editingCategory !== null || newCategory.name.trim() !== '';
    return false;
  };

  const handleTabClick = (tab) => {
    if (tab === activeTab) return;
    if (isDirty()) {
      setUnsavedDialog({ open: true, targetTab: tab });
    } else {
      setActiveTab(tab);
    }
  };

  const handleUnsavedDiscard = () => {
    const { targetTab } = unsavedDialog;
    setUnsavedDialog({ open: false, targetTab: null });
    drugDirtyRef.current = false;
    setEditingDrug(null);
    setEditingCategory(null);
    setNewDrug({ genericName: '', displayName: '' });
    setNewCategory({ name: '', imagePath: '' });
    setActiveTab(targetTab);
    showNotification(`השינויים בטאב "${TAB_NAMES[activeTab]}" לא נשמרו`, 'info');
  };

  const handleUnsavedSave = async () => {
    const tabName = TAB_NAMES[activeTab];
    const save = drugSaveRef.current;
    if (save) await save();
    const { targetTab } = unsavedDialog;
    setUnsavedDialog({ open: false, targetTab: null });
    setActiveTab(targetTab);
    showNotification(`השינויים בטאב "${tabName}" נשמרו בהצלחה`, 'success');
  };

  return (
    <CommonLayout>
      <Box sx={{ display: 'flex', gap: 4, height: '100%', direction: 'rtl' }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div className="antibiogram-wrapper">
        {/* Header with App Link */}
        <div className="antibiogram-header-container">
          <Typography variant="h4" component="h1" className="header-title">
            אנטיביוגרם
          </Typography>
          <a
            href={getAppLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="app-link"
          >
            לאפליקציה <ArrowLeftIcon />
          </a>
        </div>

        {/* Hospital Selector - only for super admins or multiple hospitals */}
        {showHospitalDropdown && (
          <div className="hospital-selector-container">
            <DropdownWithIcon
              icon={hospitalIcon}
              placeholder="בית חולים"
              value={selectedHospital}
              onChange={handleHospitalChange}
            >
              {user?.isSuperAdmin ? (
                availableHospitals.map((hospital) => (
                  <option key={hospital._id} value={hospital.nameEnglish}>
                    {hospital.nameHebrew}
                  </option>
                ))
              ) : (
                hospitalsWithEditAccess.map((hospital) => (
                  <option key={hospital._id} value={hospital.nameEnglish}>
                    {hospital.nameHebrew}
                  </option>
                ))
              )}
            </DropdownWithIcon>
          </div>
        )}

        {/* Loading Overlay */}
        <Loader isLoading={loading} />



        {/* General description */}
        {!isEditingProtocol && (
          <div className="antibiogram-description">
            מערכת ניהול האנטיביוגרם מאפשרת לכם להגדיר את הנתונים הרפואיים שישפיעו על המלצות האנטיביוטיקה באפליקציה.
            כאן תוכלו לנהל קטגוריות, תרופות, פרוטוקולים ואישורי שימוש בתרופות.
          </div>
        )}

        {/* Tabs */}
        {!isEditingProtocol && (
          <div className="antibiogram-tabs">
            {user?.isSuperAdmin && (
              <button
                className={`tab-button ${activeTab === 'categories' ? 'active' : ''}`}
                onClick={() => handleTabClick('categories')}
              >
                <img src={coinsIcon} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                קטגוריות
              </button>
            )}
            <button
              className={`tab-button ${activeTab === 'drugs' ? 'active' : ''}`}
              onClick={() => handleTabClick('drugs')}
            >
              <img src={layoutIcon} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />
              תרופות
            </button>
            <button
              className={`tab-button ${activeTab === 'protocols' ? 'active' : ''}`}
              onClick={() => handleTabClick('protocols')}
            >
              <img src={dropletIcon} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />
              פרוטוקולים
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="antibiogram-content">
          {/* Categories Section - Only for super admins */}
          {activeTab === 'categories' && user?.isSuperAdmin && (
            <CategoriesTab
              categories={categories}
              editingCategory={editingCategory}
              newCategory={newCategory}
              setNewCategory={setNewCategory}
              handleAddCategory={handleAddCategory}
              handleEditCategory={handleEditCategory}
              handleSaveCategory={handleSaveCategory}
              handleDeleteCategory={handleDeleteCategory}
              setEditingCategory={setEditingCategory}
            />
          )}

          {/* Drugs Section */}
          {activeTab === 'drugs' && (
            <DrugsTab
              drugs={drugs}
              editingDrug={editingDrug}
              newDrug={newDrug}
              setNewDrug={setNewDrug}
              handleAddDrug={handleAddDrug}
              handleEditDrug={handleEditDrug}
              handleSaveDrug={handleSaveDrug}
              handleDeleteDrug={handleDeleteDrug}
              setEditingDrug={setEditingDrug}
              blacklists={blacklists}
              onPreviewChange={handleDrugPreviewChange}
              saveRef={drugSaveRef}
              dirtyRef={drugDirtyRef}
            />
          )}

          {/* Protocols Section */}
          {activeTab === 'protocols' && (
            <ProtocolsSection
              protocols={protocols}
              setProtocols={handleSetProtocols}
              drugs={drugs}
              categories={categories}
              blacklists={blacklists}
              getDrugDisplayName={getDrugDisplayName}
              saveAntibiogramData={saveAntibiogramData}
              saving={saving}
              onEditingChange={setIsEditingProtocol}
              onFocusChange={handleFocusChange}
            />
          )}
        </div>
      </div>
      </Box>

      <FloatingPreview>
        <PreviewPane 
          focusedProtocol={focusedProtocol}
          focusedDiagnosis={focusedDiagnosis}
          allProtocols={protocols}
          allDrugs={previewDrugs}
          blacklists={previewBlacklists}
          categories={categories}
        />
      </FloatingPreview>
    </Box>

      {/* Unsaved Changes Dialog */}
      {unsavedDialog.open && (
        <div className="confirmation-dialog-overlay">
          <div className="confirmation-dialog" dir="rtl" style={{ maxWidth: '560px', width: '90%' }}>
            <h2 className="confirmation-dialog-title" style={{ fontSize: '1.3rem' }}>
              {`ישנם שינויים שלא נשמרו בטאב "${TAB_NAMES[activeTab]}"`}
            </h2>
            <p className="confirmation-dialog-message">מה ברצונך לעשות?</p>
            <div className="confirmation-dialog-actions">
              <button onClick={() => setUnsavedDialog({ open: false, targetTab: null })} style={{ background: '#fff', color: '#103C6E', border: '1px solid #103C6E', borderRadius: '8px', padding: '10px 24px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Rubik, sans-serif', fontSize: '1rem' }}>
                ביטול
              </button>
              <button onClick={handleUnsavedDiscard} style={{ background: '#fff', color: '#103C6E', border: '1px solid #103C6E', borderRadius: '8px', padding: '10px 24px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Rubik, sans-serif', fontSize: '1rem' }}>
                המשך ללא שמירה
              </button>
              <button onClick={handleUnsavedSave} style={{ background: '#103C6E', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Rubik, sans-serif', fontSize: '1rem' }}>
                שמור ועבור
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Notification (fixed position for errors) */}
      {notification.open && (
        <div className={`notification ${notification.severity}`} dir="rtl">
          <img src={infoIcon} alt="" style={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0 }} />
          {notification.message}
        </div>
      )}


    </CommonLayout>
  );
};

export default AntibiogramConfig;
